const InputRulesPlugin = {
    /**
     * Input Rules Plugin
     * 
     * This plugin's sole purpose is to perform input validations. It provides a collection of validation rules
     * that can be used across components to validate different types of input data.
     * 
     * IMPORTANT:
     * - This plugin ONLY returns true or false
     * - It does NOT return any validation messages
     * - All validation messages should be handled in the components using the translation system
     * 
     * Usage example in a component:
     * v-text-field(
     *   :rules="[
     *     v => $inputRules({value: v}, 'filled') || $t('validation.field.required'),
     *     v => $inputRules({value: v, minSize: 3}, 'fieldLength') || $t('validation.field.minLength')
     *   ]"
     * )
     */

    /**
     * Install the inputRules plugin.
     * @param {Object} Vue - The Vue instance.
     */
    install(Vue) {
        /**
         * Validate input based on specified rules.
         * @param {Object} params - The validation parameters
         * @param {*} params.value - The value to validate
         * @param {number} [params.minSize] - Minimum size for fieldLength validation
         * @param {number} [params.maxSize] - Maximum size for fieldLength validation
         * @param {boolean} [params.trim] - Whether to trim for fieldLength validation
         * @param {string} type - The validation type to perform
         * @returns {boolean} True if validation passes, false otherwise
         */
        Vue.prototype.$inputRules = function (params = {}, type) {
            // Validation type is required
            if (!type) {
                // Catch missing validation rule, for development purposes
                throw new Error('Validation rule is required');
            }

            // Value is required for all validations except nullable
            if (type === 'nullable' && params.value === undefined) {
                return true;
            }

            // Define validation mapping with required parameters
            const validations = {
                filled: () => isFilled(params.value),
                fieldLength: () => validateFieldLength(params.value, params.minSize, params.maxSize, params.trim),
                integer: () => validateInteger(params.value),
                float: () => validateFloat(params.value),
                floatTwoDecimal: () => validateFloatTwoDecimal(params.value),
                bigInteger: () => validateBigInteger(params.value),
                doubleMySQL: () => validateMySQLDouble(params.value),
                age: () => validateAge(params.value),
                '0|1': () => ceroOrOne(params.value),
                percentage: () => percentage(params.value),
                time: () => validateTime(params.value),
                nullable: () => validateNullable(params.value),
                numericBetween: () => validateNumericBetween(params.value, params.minValue, params.maxValue),
                numericCompare: () => validateNumericCompare(params.value, params.valueCompare, params.conditionCompare)
            };

            // Check if validation type exists
            if (!(type in validations)) {
                // Catch missing validation rule, for development purposes
                throw new Error(`Invalid validation rule: ${type}`);
            }

            // Execute the validation
            try {
                return validations[type]();
            } catch (error) {
                return false;
            }
        };

        /**
         * Validate that a string is not empty (contains at least one non-whitespace character).
         * @param {string} value - The input value to validate.
         * @returns {boolean} True if the string is not empty, otherwise false.
         */
        function isFilled(value) {
            // Return false if value is undefined
            if (value === undefined) {
                return false;
            }

            // Check if value contains any non-whitespace character
            return /\S/.test(value);
        }

        /**
         * Validate a string's length is within the specified range.
         * @param {string} value - The input value to validate.
         * @param {number} [minSize=0] - The minimum size of the string (default: 0).
         * @param {number} [maxSize=Number.MAX_SAFE_INTEGER] - The maximum size of the string (default: Number.MAX_SAFE_INTEGER).
         * @param {boolean} [trim=false] - Whether to trim the string before validation.
         * @returns {boolean} True if the value is valid, otherwise false.
         */
        function validateFieldLength(value, minSize = 0, maxSize = Number.MAX_SAFE_INTEGER, trim = false) {

            // Handle null/undefined values
            if (value == null && minSize > 0) return false;
            
            // Convert to string if not already
            const strValue = String(value);
            
            // Validate parameters
            if (minSize < 0) return false;
            if (maxSize < minSize) return false;
            
            // Get the value to test
            const testValue = trim ? strValue.trim() : strValue;
            
            // Test the length directly instead of using regex for better performance
            const length = testValue.length;

            return length >= minSize && length <= maxSize;
        }

        /**
         * Validate an integer value.
         * @param {string} value - The input value to validate.
         * @returns {boolean} True if the value is a valid integer, otherwise false.
         */
        function validateInteger(value) {
            const pattern = new RegExp("^[0-9]+$");
            const maxPositiveIntegerValue = 2147483647;

            return pattern.test(value) && value <= maxPositiveIntegerValue;
        }

        /**
         * Validate a numeric value.
         * @param {string} value - The input value to validate.
         * @returns {boolean} True if the value is a valid numeric value, otherwise false.
         */
        function validateFloat(value) {
            const pattern = new RegExp("^[0-9]+(\\.[0-9]+)?$");
            const maxPositiveIntegerValue = 2147483647;

            return pattern.test(value) && parseFloat(value) <= maxPositiveIntegerValue;
        }

        /**
         * Validates if a given value is a valid float with up to two decimal places.
         * @param {string} value - The input value to validate.
         * @return {boolean} True if the value is a valid float with up to two decimal places, false otherwise.
         */
        function validateFloatTwoDecimal(value) {
            const pattern = new RegExp("^[0-9]+(\\.[0-9]{1,2})?$");
            const maxPositiveIntegerValue = 2147483647;

            return pattern.test(value) && parseFloat(value) <= maxPositiveIntegerValue;
        }

        /**
         * Validate a big integer value.
         * @param {string} value - The input value to validate.
         * @returns {boolean} True if the value is a valid big integer, otherwise false.
         */
        function validateBigInteger(value) {
            const pattern = new RegExp("^[0-9]+$");
            const maxPositiveBigIntegerValue = 9223372036854775807;

            return pattern.test(value) && value < maxPositiveBigIntegerValue;
        }

        /**
         * Validate a MySQL Double value.
         * @param {string} value - The input value to validate.
         * @returns {boolean} True if the value is a valid MySQL double, otherwise false.
         */
        function validateMySQLDouble(value) {
            const regex = /^\d{1,8}(\.\d{1,2})?$/;
            return regex.test(value);
        }

        /**
         * Validate that a value is 0 or 1.
         * @param {string} value - The input value to validate.
         * @returns {boolean} True if the value is 0 or 1, otherwise false.
         */
        function ceroOrOne(value) {
            const pattern = new RegExp("^[0-1]$");

            return pattern.test(value);
        }

        /**
         * Validate that an age is between 0 and 124.
         * @param {integer} value - The input value to validate.
         * @returns {boolean} True if the age is valid, otherwise false.
         */
        function validateAge(value) {
            return (value >= 0 && value <= 124);
        }

        /**
         * Validate that a value is between 0 and 100.
         * @param {integer} value - The input value to validate.
         * @returns {boolean} True if the percentage is valid, otherwise false.
         */
        function percentage(value) {
            return (value > 0 && value <= 100);
        }

        /**
         * Validates if a given time string is in the correct format (HH:mm:ss).
         * @param {string} value - The input time string to validate.
         * @return {boolean} True if the time format is valid, false otherwise.
         */
        function validateTime(value) {
            const timeReg = /((([0-1]{1}\d{1})|(2[0-3]{1})):([0-5]{1}\d{1}):([0-5]{1}\d{1}))/g;
            return (value || "").length == 8 && timeReg.test(value);
        }

        /**
         * Validates if a value can be null/empty.
         * @param {*} value - The input value to validate.
         * @returns {boolean} True if the value is empty/null, false otherwise.
         */
        function validateNullable(value) {
            return (value || "").toString().length == 0;
        }

        /**
         * Validates a number with a minimum and a maximum value.
         * @param {integer} value - The input value to validate.
         * @param {number} minValue - The minimum value of the number.
         * @param {number} maxValue - The maximum value of the number.
         */
        function validateNumericBetween(value, minValue, maxValue) {
            const _number = parseInt(value);
            if((value || '').length == 0 || value == '0' || (!isNaN(_number) && Number.isInteger(_number) && _number.toString() == value)) {
                return (value || '').length == 0 || (value >= minValue && value <= maxValue);
            }
            return false;
        }

        /**
         * Validates a number with another number referring to a condition.
         * @param {integer} value - The input value to validate.
         * @param {number} valueCompare - The compare value of the number.
         * @param {string} conditionCompare - The comparison condition string for number.
         */
        function validateNumericCompare(value, valueCompare, conditionCompare) {
            const _number = (value || '').length > 0 ? parseInt(value) : 0;
            if((value || '').length == 0 || value == '0' || (!isNaN(_number) && Number.isInteger(_number) && _number.toString() === value)) {
                if((value || '').length == 0 || value == '0') return true;
                const _numberCompare = (valueCompare || '').length > 0 ? parseInt(valueCompare) : 0;
                switch(conditionCompare) {
                    case "=":
                        return _number == _numberCompare;
                    case ">":
                        return _number > _numberCompare;
                    case "<":
                        return _number < _numberCompare;
                    case ">=":
                        return _number >= _numberCompare;
                    case "<=":
                        return _number <= _numberCompare;
                    default:
                        break;
                }
            }
            return false;
        }
    },
};

export default InputRulesPlugin;